/**
 * Host-side RPC facade for the dsh-connect web settings pane.
 *
 * Mirrors dsh-im's host RPC pattern: a plugin registers a single channel on
 * `ctx.connection.rpc.handle(...)` and a handler dispatches dotted endpoints,
 * returning `{ ok: true, value }` on success or `{ ok: false, error }` on
 * failure (see dsh-im `plugin-src/host/update-rpc.mjs`).
 *
 * Endpoints:
 * - `settings.get` — current non-secret config + enabled channels + credential presence.
 * - `settings.save` — persist non-secret config (payload must be non-empty).
 * - `credentials.save` — persist secrets to the DSH credential store (payload
 *   `{ channel, values }`, values are {configKey: secret}).
 * - `settings.status` — fresh status snapshot.
 * - `onboarding.start` / `onboarding.status` / `onboarding.cancel` — the
 *   one-click bot creation flow. `start` answers with the *current* state and
 *   the flow goes on living in the host process; the pane polls `status` until
 *   a terminal phase, so a page refresh cannot abort a single-use
 *   device-authorization link. See `./feishu-onboarding`.
 *
 * @module dsh-connect/settings/settings-rpc
 */
import type { IncomingMessage, ServerResponse } from "node:http";
import type { OnboardingStatus } from "./feishu-onboarding.js";
import type { ChannelConnectionStatus } from "./channel-status.js";

/** RPC channel the settings pane calls. */
export const SETTINGS_RPC_CHANNEL = "/dsh-connect";

/** Endpoints the settings pane can invoke on the channel. */
export const SETTINGS_ENDPOINTS = Object.freeze([
  "settings.get", "settings.save", "credentials.save", "settings.status",
  "onboarding.start", "onboarding.status", "onboarding.cancel",
]);

/** Error codes considered safe to surface verbatim to the browser. */
const PUBLIC_ERRORS = new Set([
  "settings-failed", "bad-request", "cancelled", "invalid-payload", "unknown-profile",
  "unsupported-runtime", "not-configured", "credential-missing", "credential-invalid",
  "channel-disabled", "save-failed", "state-unavailable", "disposed", "interrupted",
  "unsupported", "invalid-channel", "invalid-credentials",
]);

/** A settings pane `settings.get`/status snapshot. */
export interface SettingsSnapshot {
  /** The current plugin config (non-secret fields). */
  config: Record<string, unknown>;
  /** The channel names currently enabled. */
  enabled: string[];
  /** Per-channel credential presence (true = stored & set). */
  credentials: Record<string, boolean>;
  /**
   * Per-channel, per-config-key secret **presence** (true = a value is stored
   * for that key) — never the values themselves. The pane uses it to tell an
   * untouched input («已配置») from an empty one.
   *
   * This used to echo the values, which put an `appSecret` into browser state
   * and every screenshot of the settings page. What leaves the host now is
   * either this boolean or the masked {@link secretPreviews} below — a usable
   * secret still leaves exactly once, when the adapter consumes it
   * (`injectSecrets`).
   */
  secrets?: Record<string, Record<string, boolean>>;
  /**
   * Per-channel, per-config-key **display preview**, for the user to confirm
   * what they configured without being able to copy it back out.
   *
   * Already masked on the host by `maskSecret` (`./secret-disclosure.js`), so
   * it is lossy by construction: identifiers such as an `appId` come through
   * whole, an `appSecret` keeps only its first and last four characters, and a
   * `webhookUrl` keeps its origin and path with the `access_token` in the query
   * masked. A key is present here only when a value is stored for it, which
   * makes an empty entry the same statement as `secrets[key] === false`.
   *
   * **Display-only.** The pane renders it as text; it must never be written
   * back as a credential, and `buildCredentialSaves` ignores it — the secret
   * inputs are still write-only and still start blank.
   */
  secretPreviews?: Record<string, Record<string, string>>;
  /**
   * True when the `dsh-connect` settings namespace is live, i.e. this config is
   * stored in `$DSH_HOME/settings.yaml` and a save takes effect immediately.
   * False means the legacy JSON state file is behind this pane: the settings
   * path control still applies, and a save needs a restart.
   */
  live?: boolean;
  /**
   * Non-fatal problems with the call that produced this snapshot. Absent when
   * there is nothing to say.
   *
   * These are the cases where the save *did* happen but did not (yet) have the
   * effect the user asked for — a rotated secret stored in the credential store
   * that the running adapter did not pick up, say. Neither answer available to
   * the service is honest on its own: reporting a failed save would be a lie
   * (the value *is* stored, and re-sending it changes nothing), and reporting a
   * clean save is the lie the user then acts on — they see 「已保存」 over a bot
   * that stays broken until restart.
   *
   * Codes come from {@link SettingsWarningCode}; the pane owns the wording, so a
   * new code needs a `w.<code>` locale key in both languages (the locale test
   * enforces that) rather than an English sentence shipped from the host.
   */
  warnings?: SettingsWarningCode[];
  /**
   * Channels whose stored credentials could not be **read**.
   *
   * Distinct from `credentials[ch] === false`, and the distinction is the whole
   * point: `false` asserts "nothing is stored", while an IO error means the
   * answer is *unknown*. Reporting absence over a `EACCES` is how a working bot
   * comes to be described as 「未配置凭据」, which sends the user off to
   * re-enter a credential that was never broken. A channel listed here renders
   * as unknown — it is deliberately not `credentials[ch] === undefined`, because
   * a missing key already means "not a channel this pane knows".
   */
  credentialErrors?: string[];
  /**
   * Channels whose adapter failed to **start** with the config just saved, as
   * `{channel: reason}`.
   *
   * A channel that throws on start is logged and the rest still come up (see
   * `ChannelRuntime`), so the save itself succeeds and the pane would otherwise
   * report 「已保存」 over a channel that is silently not running. The reason is
   * the adapter's own message — passed through rather than mapped to a code,
   * because it is the only part that identifies *which* credential is wrong.
   */
  channelErrors?: Record<string, string>;
  /**
   * Per-channel **access** state — whether the channel is switched on, up, or
   * actually connected — as `{channel: {state, attempts?}}`.
   *
   * Kept separate from `credentials`, which only says a secret exists: a channel
   * with perfect credentials and a socket that keeps dropping is 「已配置凭据」 +
   * 「重连中」, and collapsing the two would describe it as working.
   *
   * **Attached whenever the host wired the option, which deliberately breaks the
   * "spread only when non-empty" convention above.** The other optional fields
   * are adds — an empty list carries no information — but here 「未启用」 *is* an
   * answer and a channel with no probe is honestly 「运行中」. So an absent key
   * has to keep its stronger meaning: *this host cannot report access state at
   * all* (an older host, or a boot where the runtime was never wired), and the
   * pane renders no badge rather than inventing one.
   */
  channelStatus?: Record<string, ChannelConnectionStatus>;
  /**
   * The DSH-wide default model, for the pane's read-only display row. Absent
   * when the host could not answer (no `agentDefaultModel` service, nothing
   * selected), in which case the pane omits the row — an empty control would
   * imply the user could set it here, and they cannot: the selection belongs to
   * DSH and changing it would move every other session with it.
   */
  agentModel?: { provider: string; model: string };
  /**
   * General-settings keys a `dsh.shared.config.json` on this machine overrides.
   * Absent when there is no shared config (the common case), so the rows render
   * as ordinary editable fields with no provenance note.
   */
  sharedOverrideKeys?: string[];
}

/**
 * Codes a snapshot's {@link SettingsSnapshot.warnings} can carry. Each one needs
 * a `w.<code>` key in `client/locale.mjs` (both languages). A real array rather
 * than a bare union so the locale test can *derive* that list instead of keeping
 * a second copy of it — a copy is exactly what goes stale.
 */
export const SETTINGS_WARNING_CODES = [
  "credentialsStoredNotApplied",
] as const;

export type SettingsWarningCode = (typeof SETTINGS_WARNING_CODES)[number];

/** The service backing the RPC; supplied by the web-settings integration. */
export interface SettingsService {
  get(): Promise<SettingsSnapshot>;
  save(config: Record<string, unknown>): Promise<SettingsSnapshot>;
  /** Persist channel secrets ({configKey: secret}) to the credential store. */
  saveCredentials?(channel: string, values: Record<string, string>): Promise<SettingsSnapshot>;
  /**
   * One-click bot creation. Optional like `saveCredentials` — a host that wired
   * no onboarding registry reports `unsupported` instead of failing to compile.
   * Returns the flow's own status shape, not a `SettingsSnapshot`: a
   * `SettingsSnapshot` is deep-compared field by field in several tests and is
   * only spread when non-empty, so a permanent new field there would cost far
   * more than this feature needs.
   */
  onboardingStart?(channel: string): Promise<OnboardingStatus>;
  onboardingStatus?(): Promise<OnboardingStatus>;
  onboardingCancel?(): Promise<OnboardingStatus>;
  status(): Promise<SettingsSnapshot>;
}

function validPayload(endpoint: string, payload: unknown): boolean {
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) return false;
  const keys = Object.keys(payload);
  if (endpoint === "settings.save") return keys.length > 0;
  if (endpoint === "credentials.save") {
    return keys.length > 0 && typeof (payload as any).channel === "string"
      && (payload as any).values !== null && typeof (payload as any).values === "object"
      && !Array.isArray((payload as any).values);
  }
  // Shape only: an unknown channel is a valid shape and the service answers for
  // it (`invalid-channel`), the same posture `credentials.save` already takes.
  if (endpoint === "onboarding.start") {
    return keys.length > 0 && typeof (payload as any).channel === "string" && (payload as any).channel !== "";
  }
  // The reads carry no payload.
  return keys.length === 0;
}

/**
 * Build the channel handler for a settings service.
 *
 * `log` is how a failure that is *not* in {@link PUBLIC_ERRORS} stays
 * diagnosable. The pane is only told a code, and an unrecognised error is
 * flattened to the generic `settings-failed` — deliberately, since the raw
 * message of a host-side failure may name plugin internals the pane cannot act
 * on. That flattening is only acceptable while the real cause is recorded
 * *somewhere*: without a line here, a refused write (a missing profile entry, a
 * non-volatile path) reaches the user as "保存失败" and leaves nothing at all in
 * the host log to work from.
 */
export function createSettingsRpcHandler(service: SettingsService, log?: (message: string) => void) {
  return async (endpoint: string, payload: unknown, signal?: { aborted?: boolean }) => {
    if (!SETTINGS_ENDPOINTS.includes(endpoint as never) || !validPayload(endpoint, payload)) {
      return { ok: false, error: { code: "bad-request", message: "Invalid settings request." } };
    }
    if (signal?.aborted) return { ok: false, error: { code: "cancelled", message: "Request cancelled." } };
    try {
      const record = (payload ?? {}) as Record<string, unknown>;
      const value = endpoint === "settings.get"
        ? await service.get()
        : endpoint === "settings.save"
          ? await service.save(record)
          : endpoint === "credentials.save"
            ? await saveCredentialsP(service, record)
            : endpoint === "onboarding.start"
              ? await onboardingStartP(service, record)
              : endpoint === "onboarding.status"
                ? await onboardingStatusP(service)
                : endpoint === "onboarding.cancel"
                  ? await onboardingCancelP(service)
                  : await service.status();
      return { ok: true, value };
    } catch (error) {
      const publicCode = (error as any)?.code;
      const code = PUBLIC_ERRORS.has(publicCode) ? publicCode : "settings-failed";
      // Always logged, including for a public code: the pane shows the code, and
      // the specific reason (`not-configured` for which ref, say) is in the
      // message. The status is dropped from the failure the pane receives, so
      // this line is the only record that the call failed on our side.
      log?.(`connect: settings rpc ${endpoint} failed (${code}): ${errorText(error)}`);
      return { ok: false, error: { code, message: code } };
    }
  };
}

/**
 * One-line error text for the diagnostic log line. The `code` is prefixed when
 * the thrown value carries one — for a domain error (`save-failed`, `unchanged`)
 * that code is the actionable half, and the message alone would read as a
 * sentence with no name.
 */
function errorText(error: unknown): string {
  const code = (error as any)?.code;
  const message = error instanceof Error ? error.message : String(error);
  return typeof code === "string" && code !== "" && !message.startsWith(code) ? `${code}: ${message}` : message;
}

/** The error a service reports for an endpoint it was not wired to serve. */
function unsupported(): never {
  const err = new Error("unsupported") as any;
  err.code = "unsupported";
  throw err;
}

async function saveCredentialsP(service: SettingsService, record: Record<string, unknown>): Promise<SettingsSnapshot> {
  if (typeof service.saveCredentials !== "function") unsupported();
  const channel = record.channel as string;
  const values = (record.values ?? {}) as Record<string, string>;
  return service.saveCredentials(channel, values);
}

async function onboardingStartP(service: SettingsService, record: Record<string, unknown>): Promise<OnboardingStatus> {
  if (typeof service.onboardingStart !== "function") unsupported();
  return service.onboardingStart(record.channel as string);
}

async function onboardingStatusP(service: SettingsService): Promise<OnboardingStatus> {
  if (typeof service.onboardingStatus !== "function") unsupported();
  return service.onboardingStatus();
}

async function onboardingCancelP(service: SettingsService): Promise<OnboardingStatus> {
  if (typeof service.onboardingCancel !== "function") unsupported();
  return service.onboardingCancel();
}

/** Endpoint segment accepted by a Connection RPC path (host-compatible). */
const ENDPOINT_SEGMENT_PATTERN = /^[A-Za-z0-9_$.-]+$/;

/** Upper bound on an accepted settings request body; settings payloads are tiny. */
const MAX_BODY_BYTES = 1024 * 1024;

/** The Host/Origin + browser-auth fence exposed by the host `connection` service. */
interface ConnectionFence {
  /** HTTP status to reject with, or `undefined` to allow the request through. */
  requestRejection?(request: IncomingMessage): number | undefined;
}

/** A named `webServer` route (subset of the host's route shape we rely on). */
interface PrefixRoute {
  kind: "prefix";
  path: string;
  handler: (req: IncomingMessage, res: ServerResponse) => void | Promise<void>;
}

/** The `webServer` host service surface this module needs. */
interface WebServerLike {
  register?(route: PrefixRoute): () => void;
}

/** The inject-scope context `installSettingsRpc` expects (connection + webServer). */
interface SettingsRpcScope {
  connection?: ConnectionFence;
  webServer?: WebServerLike;
  effect?<T>(execute: () => T, label?: string): (() => void) | T;
  logger?: { warn?(message: string): void };
}

/**
 * Derive the endpoint from a request path, exactly like the host's own
 * Connection router: the remainder of `channel/` must be non-empty segments
 * matching {@link ENDPOINT_SEGMENT_PATTERN}.
 */
function endpointFromPath(channel: string, pathname: string): string | undefined {
  if (!pathname.startsWith(`${channel}/`)) return undefined;
  const endpoint = pathname.slice(channel.length + 1);
  const segments = endpoint.split("/");
  if (segments.some((segment) => segment === "" || segment === "." || segment === ".." || !ENDPOINT_SEGMENT_PATTERN.test(segment))) return undefined;
  return endpoint;
}

/** Read a request body as UTF-8 text, rejecting past {@link MAX_BODY_BYTES}. */
function readBody(req: IncomingMessage, limit = MAX_BODY_BYTES): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    let size = 0;
    let overLimit = false;
    req.on("data", (chunk: Buffer) => {
      if (overLimit) return;
      size += chunk.length;
      if (size > limit) {
        overLimit = true;
        chunks.length = 0;
        reject(new Error("body exceeds limit"));
        return;
      }
      chunks.push(chunk);
    });
    req.on("end", () => resolve(Buffer.concat(chunks).toString("utf8")));
    req.on("error", reject);
  });
}

/** Write a JSON response with an accurate content length. */
function sendJson(res: ServerResponse, status: number, body: unknown): void {
  const text = JSON.stringify(body);
  res.writeHead(status, { "content-type": "application/json", "content-length": String(Buffer.byteLength(text)) });
  res.end(text);
}

/**
 * Coerce a handler result into the Connection response envelope the browser
 * client validates. `error.details` must always be an object — the client's
 * `parseConnectionResponse` throws on a missing one.
 */
function responseEnvelope(rpcId: string, result: { ok?: boolean; value?: unknown; error?: unknown }): unknown {
  if (result?.ok === true) return { type: "server-response", rpcId, result: { ok: true, value: result.value } };
  const error = (result?.error ?? {}) as { code?: unknown; message?: unknown; details?: unknown };
  const details = error.details !== null && typeof error.details === "object" ? error.details : {};
  return {
    type: "server-response",
    rpcId,
    result: { ok: false, error: { code: String(error.code ?? "settings-failed"), message: String(error.message ?? ""), details } },
  };
}

/**
 * Build the raw HTTP handler for {@link SETTINGS_RPC_CHANNEL}. Mirrors the
 * host's own Connection RPC adapter (method/content-type/envelope checks plus
 * the Host/Origin and browser-auth fence) so the pane sees the same protocol
 * and the same failure codes as a first-party channel.
 */
export function createSettingsHttpHandler(service: SettingsService, connection?: ConnectionFence, log?: (message: string) => void) {
  const handle = createSettingsRpcHandler(service, log);

  return async function settingsHttpHandler(req: IncomingMessage, res: ServerResponse): Promise<void> {
    // Same fence the host applies to `/api`: trusted Host/Origin, then browser auth.
    const rejection = connection?.requestRejection?.(req);
    if (rejection !== undefined) {
      res.writeHead(rejection, { "content-type": "text/plain" });
      res.end(rejection === 401 ? "unauthorized" : "forbidden");
      return;
    }

    const endpoint = endpointFromPath(SETTINGS_RPC_CHANNEL, new URL(req.url ?? "/", "http://dsh.internal").pathname);
    if (req.method !== "POST" || endpoint === undefined) {
      res.writeHead(404);
      res.end();
      return;
    }

    const contentType = String(req.headers["content-type"] ?? "").split(";", 1)[0]?.trim().toLowerCase();
    if (contentType !== "application/json") {
      res.writeHead(415);
      res.end("content type must be application/json");
      return;
    }

    let raw: string;
    try {
      raw = await readBody(req);
    } catch {
      res.writeHead(400);
      res.end("body is not JSON");
      return;
    }

    let body: unknown;
    try {
      body = JSON.parse(raw);
    } catch {
      res.writeHead(400);
      res.end("body is not JSON");
      return;
    }

    const envelope = (body ?? {}) as { type?: unknown; rpcId?: unknown; method?: unknown; payload?: unknown };
    const rpcId = typeof envelope.rpcId === "string" ? envelope.rpcId : "invalid-request";
    if (envelope.type !== "client-request" || typeof envelope.rpcId !== "string" || typeof envelope.method !== "string") {
      sendJson(res, 200, responseEnvelope(rpcId, { ok: false, error: { code: "bad-request", message: "invalid client-request message" } }));
      return;
    }
    if (envelope.method !== endpoint) {
      sendJson(res, 200, responseEnvelope(rpcId, {
        ok: false,
        error: { code: "bad-request", message: `method ${JSON.stringify(envelope.method)} does not match endpoint ${JSON.stringify(endpoint)}` },
      }));
      return;
    }

    // Abort the caller's signal when the browser goes away before we answer.
    const aborter = new AbortController();
    res.on("close", () => {
      if (!res.writableEnded) aborter.abort();
    });

    const result = await handle(endpoint, envelope.payload, aborter.signal) as { ok?: boolean; value?: unknown; error?: unknown };
    sendJson(res, 200, responseEnvelope(rpcId, result));
  };
}

/**
 * Mount the settings RPC channel on the host webserver.
 *
 * Mounts a `prefix` route for {@link SETTINGS_RPC_CHANNEL} directly on the
 * `webServer` host service — the same thing the host's own
 * `connection.rpc.handle()` does internally (`owner.webServer.register(route)`
 * with the `/api` fence), and the same thing `dsh-api-gateway` does for its
 * WebSocket route.
 *
 * Why not `connection.rpc.handle()`: the host's `HostConnectionService` is
 * context-tracked, so its `get rpc()` captures `this.ctx`. cordis resolves that
 * read to a *shadow* of the service's construction context, and service
 * resolution then walks **that** fiber chain — which never contains the
 * `webServer` provider (a sibling fiber). The call therefore dies with
 * `cannot get property "webServer" without inject`, the route is never mounted,
 * and every `settings.*` call falls through to the static fallback (HTTP 405),
 * leaving the pane stuck on "加载中". Injecting `webServer` into the calling
 * scope — or declaring it in the plugin's `inject` — does not help, because the
 * shadow overrides the caller's fiber.
 *
 * @param ctx - an injection scope holding `connection` and `webServer`; its
 *   `logger` is used, when present, to record a refused call (see
 *   {@link createSettingsRpcHandler}).
 * @param options - the settings service backing the channel.
 * @returns a disposer removing the route (no-op if the host has no webserver).
 */
export function installSettingsRpc(ctx: unknown, options: { service: SettingsService }): () => void {
  const scope = (ctx ?? {}) as SettingsRpcScope;
  const register = scope.webServer?.register;
  if (typeof register !== "function") return () => {};

  const log = (message: string): void => scope.logger?.warn?.(message);
  const route: PrefixRoute = {
    kind: "prefix",
    path: SETTINGS_RPC_CHANNEL,
    handler: createSettingsHttpHandler(options.service, scope.connection, log),
  };

  // Register through the scope's effect so the route is torn down with the fiber.
  if (typeof scope.effect === "function") {
    const disposed = scope.effect(() => register.call(scope.webServer, route), `dsh-connect: ${SETTINGS_RPC_CHANNEL} settings rpc`);
    return typeof disposed === "function" ? (disposed as () => void) : () => {};
  }
  return register.call(scope.webServer, route);
}